import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  TextField,
  InputAdornment,
  Stack,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Receipt as ReceiptIcon,
  Info as InfoIcon,
  OpenInNew as OpenInNewIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import axios from 'axios';

// Utility function for currency formatting
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount / 100); // Assuming amount is in cents
};

// Status chip component
const StatusChip = ({ status }) => {
  const statusConfig = {
    active: { label: 'Active', color: 'success' },
    none: { label: 'None', color: 'default' },
    cancelled: { label: 'Cancelled', color: 'error' },
    payment_failed: { label: 'Failed', color: 'error' }
  };

  const config = statusConfig[status] || { label: status, color: 'default' };

  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
    />
  );
};

function BillingManagement({ onNotification }) {
  // State
  const [billingData, setBillingData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPeriod, setSelectedPeriod] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageData, setUsageData] = useState(null);
  
  // Use ref to prevent concurrent fetches without causing a rerender
  const isFetchingRef = useRef(false);
  const requestParamsRef = useRef({});
  const timeoutRef = useRef(null);

  // Memoize request parameters to detect if they've changed
  const requestParams = {
    page: page + 1,
    limit: rowsPerPage,
    search: searchQuery,
    month: selectedPeriod.month,
    year: selectedPeriod.year
  };

  // Fetch billing data - remove loading from dependencies
  const fetchBillingData = useCallback(async () => {
    // Skip if already fetching
    if (isFetchingRef.current) return;
    
    // Skip if params haven't changed and we already have data
    const sameParams = JSON.stringify(requestParams) === JSON.stringify(requestParamsRef.current);
    if (sameParams && billingData.length > 0) return;
    
    // Update current params
    requestParamsRef.current = { ...requestParams };
    
    // Start fetch
    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const response = await axios.get('/api/admin/billing', {
        params: requestParams
      });

      if (response.data.status === 'success') {
        setBillingData(response.data.data.items);
        setTotalItems(response.data.data.total);
      } else {
        throw new Error(response.data.message || 'Failed to fetch billing data');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch billing data');
      onNotification('Error', err.message || 'Failed to fetch billing data', 'error');
    } finally {
      setLoading(false);
      isFetchingRef.current = false;
    }
  }, [page, rowsPerPage, searchQuery, selectedPeriod, onNotification, billingData.length]);

  // Load data only when parameters change
  useEffect(() => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set a new timeout to debounce the fetch
    timeoutRef.current = setTimeout(() => {
      fetchBillingData();
    }, 300);
    
    // Clean up the timeout on component unmount or when dependencies change
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [fetchBillingData]);

  // Manually trigger a refresh
  const handleRefresh = useCallback(() => {
    // Force a refresh by clearing the request params cache
    requestParamsRef.current = {};
    
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Fetch immediately without debounce for user-initiated refresh
    fetchBillingData();
  }, [fetchBillingData]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handlePeriodChange = (event) => {
    const [year, month] = event.target.value.split('-');
    setSelectedPeriod({
      year: parseInt(year),
      month: parseInt(month)
    });
    setPage(0);
  };

  const handleSyncBilling = async () => {
    try {
      setSyncLoading(true);
      setError(null);

      const response = await axios.post('/api/admin/billing/sync', {
        year: selectedPeriod.year,
        month: selectedPeriod.month
      });

      if (response.data.status === 'success') {
        onNotification(
          'Sync Successful',
          'Billing data has been synchronized successfully',
          'success'
        );
        // Force a refresh
        handleRefresh();
      } else {
        throw new Error(response.data.message || 'Failed to sync billing data');
      }
    } catch (err) {
      setError(err.message || 'Failed to sync billing data');
      onNotification('Error', err.message || 'Failed to sync billing data', 'error');
    } finally {
      setSyncLoading(false);
      setSyncDialogOpen(false);
    }
  };

  // Handle opening the invoice details dialog
  const handleOpenInvoiceDialog = async (item) => {
    try {
      setSelectedInvoice(item);
      setInvoiceDialogOpen(true);
      setInvoiceLoading(true);
      
      // Fetch invoice details from Stripe
      const response = await axios.get(`/api/admin/billing/${item._id}/invoice`);
      
      if (response.data.status === 'success') {
        setInvoiceData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch invoice details');
      }
    } catch (err) {
      setInvoiceData(null);
      onNotification('Error', err.message || 'Failed to fetch invoice details', 'error');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // Handle closing the invoice dialog
  const handleCloseInvoiceDialog = () => {
    setInvoiceDialogOpen(false);
    setSelectedInvoice(null);
    setInvoiceData(null);
  };

  // Handle opening the usage details dialog
  const handleOpenUsageDialog = async (item) => {
    try {
      setSelectedUser(item);
      setUsageDialogOpen(true);
      setUsageLoading(true);
      
      // Fetch detailed usage statistics
      const response = await axios.get(`/api/admin/billing/usage/${item._id}`);
      
      if (response.data.status === 'success') {
        setUsageData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to fetch usage details');
      }
    } catch (err) {
      setUsageData(null);
      onNotification('Error', err.message || 'Failed to fetch usage details', 'error');
    } finally {
      setUsageLoading(false);
    }
  };

  // Handle closing the usage dialog
  const handleCloseUsageDialog = () => {
    setUsageDialogOpen(false);
    setSelectedUser(null);
    setUsageData(null);
  };

  // Format date for display
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Generate period options for the last 12 months
  const periodOptions = Array.from({ length: 12 }, (_, i) => {
    const date = new Date();
    date.setMonth(date.getMonth() - i);
    return {
      value: `${date.getFullYear()}-${date.getMonth() + 1}`,
      label: date.toLocaleString('default', { month: 'long', year: 'numeric' })
    };
  });

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flex={1}>
          <TextField
            placeholder="Search users..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            sx={{ minWidth: 200 }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Billing Period</InputLabel>
            <Select
              value={`${selectedPeriod.year}-${selectedPeriod.month}`}
              onChange={handlePeriodChange}
              label="Billing Period"
            >
              {periodOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            variant="outlined"
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            startIcon={<SyncIcon />}
            onClick={() => setSyncDialogOpen(true)}
            variant="contained"
            disabled={loading || syncLoading}
          >
            Sync Billing
          </Button>
        </Stack>
      </Stack>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Billing Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Service Tier</TableCell>
              <TableCell>Usage</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Last Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : billingData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No billing data found
                </TableCell>
              </TableRow>
            ) : (
              billingData.map((item) => (
                <TableRow key={item._id}>
                  <TableCell>
                    <Typography variant="body2">{item.username}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {item.email}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ textTransform: 'capitalize' }}>
                    {item.serviceTier}
                  </TableCell>
                  <TableCell>
                    {item.usage?.total || 0} calls
                  </TableCell>
                  <TableCell>
                    {formatCurrency(item.amount || 0)}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={item.status} />
                  </TableCell>
                  <TableCell>
                    {new Date(item.updatedAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenInvoiceDialog(item)}
                      title="View Invoice"
                    >
                      <ReceiptIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenUsageDialog(item)}
                      title="View Usage Details"
                    >
                      <InfoIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalItems}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      {/* Invoice Details Dialog */}
      <Dialog
        open={invoiceDialogOpen}
        onClose={handleCloseInvoiceDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Invoice Details
          {selectedInvoice && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedInvoice.username} - {formatDate(selectedInvoice.updatedAt)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {invoiceLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !invoiceData ? (
            <Alert severity="info">
              No invoice data available for this record.
            </Alert>
          ) : (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>Summary</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Amount</Typography>
                    <Typography variant="h5">{formatCurrency(invoiceData.amount_due || 0)}</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center' }}>
                      <StatusChip status={invoiceData.status} />
                    </Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Invoice Date</Typography>
                    <Typography variant="h5">{formatDate(invoiceData.created * 1000)}</Typography>
                  </Paper>
                </Stack>
              </Box>

              <Typography variant="h6" gutterBottom>Line Items</Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Quantity</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Amount</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoiceData.lines?.data?.map((line, index) => (
                      <TableRow key={index}>
                        <TableCell>{line.description}</TableCell>
                        <TableCell align="right">{line.quantity || 1}</TableCell>
                        <TableCell align="right">
                          {formatCurrency((line.amount / (line.quantity || 1)) || 0)}
                        </TableCell>
                        <TableCell align="right">{formatCurrency(line.amount || 0)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                        Total
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(invoiceData.amount_due || 0)}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Invoice ID: {invoiceData.id}
                </Typography>
                <Stack direction="row" spacing={1}>
                  {invoiceData.hosted_invoice_url && (
                    <Button
                      startIcon={<OpenInNewIcon />}
                      variant="outlined"
                      size="small"
                      href={invoiceData.hosted_invoice_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      View in Stripe
                    </Button>
                  )}
                  {invoiceData.invoice_pdf && (
                    <Button
                      startIcon={<DownloadIcon />}
                      variant="contained"
                      size="small"
                      href={invoiceData.invoice_pdf}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download PDF
                    </Button>
                  )}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseInvoiceDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Usage Details Dialog */}
      <Dialog
        open={usageDialogOpen}
        onClose={handleCloseUsageDialog}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Usage Details
          {selectedUser && (
            <Typography variant="subtitle2" color="text.secondary">
              {selectedUser.username} - {selectedUser.serviceTier} Tier
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {usageLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : !usageData ? (
            <Alert severity="info">
              No usage data available for this user.
            </Alert>
          ) : (
            <Box>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom>Usage Summary</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
                  <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Total API Calls</Typography>
                    <Typography variant="h5">{usageData.summary.total.toLocaleString()}</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Monthly Limit</Typography>
                    <Typography variant="h5">{usageData.tierLimits.monthlyLimit.toLocaleString()}</Typography>
                  </Paper>
                  <Paper sx={{ p: 2, flex: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary">Usage %</Typography>
                    <Typography variant="h5">
                      {Math.round((usageData.summary.total / usageData.tierLimits.monthlyLimit) * 100)}%
                    </Typography>
                  </Paper>
                </Stack>
              </Box>

              <Typography variant="h6" gutterBottom>API Call Breakdown</Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Operation Type</TableCell>
                      <TableCell align="right">Count</TableCell>
                      <TableCell align="right">% of Total</TableCell>
                      <TableCell align="right">Base Limit</TableCell>
                      <TableCell align="right">Overage</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>Retrieval</TableCell>
                      <TableCell align="right">{usageData.summary.retrieval.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.round((usageData.summary.retrieval / usageData.summary.total) * 100)}%
                      </TableCell>
                      <TableCell align="right">{usageData.tierLimits.monthlyLimit.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.max(0, usageData.summary.retrieval - usageData.tierLimits.monthlyLimit).toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Submission</TableCell>
                      <TableCell align="right">{usageData.summary.submission.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.round((usageData.summary.submission / usageData.summary.total) * 100)}%
                      </TableCell>
                      <TableCell align="right">{usageData.tierLimits.monthlyLimit.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.max(0, usageData.summary.submission - usageData.tierLimits.monthlyLimit).toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Amendment</TableCell>
                      <TableCell align="right">{usageData.summary.amendment.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.round((usageData.summary.amendment / usageData.summary.total) * 100)}%
                      </TableCell>
                      <TableCell align="right">{usageData.tierLimits.monthlyLimit.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.max(0, usageData.summary.amendment - usageData.tierLimits.monthlyLimit).toLocaleString()}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Retraction</TableCell>
                      <TableCell align="right">{usageData.summary.retraction.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.round((usageData.summary.retraction / usageData.summary.total) * 100)}%
                      </TableCell>
                      <TableCell align="right">{usageData.tierLimits.monthlyLimit.toLocaleString()}</TableCell>
                      <TableCell align="right">
                        {Math.max(0, usageData.summary.retraction - usageData.tierLimits.monthlyLimit).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" gutterBottom>Usage by API Key</Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>API Key</TableCell>
                      <TableCell>Label</TableCell>
                      <TableCell align="right">Total Calls</TableCell>
                      <TableCell align="right">% of Usage</TableCell>
                      <TableCell align="right">Last Used</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageData.byApiKey.map((keyData) => (
                      <TableRow key={keyData.apiKeyId}>
                        <TableCell>{keyData.apiKeyId.slice(-8)}</TableCell>
                        <TableCell>{keyData.label || 'Unnamed'}</TableCell>
                        <TableCell align="right">{keyData.total.toLocaleString()}</TableCell>
                        <TableCell align="right">
                          {Math.round((keyData.total / usageData.summary.total) * 100)}%
                        </TableCell>
                        <TableCell align="right">{formatDate(keyData.lastUsed)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="h6" gutterBottom>Monthly Trend</Typography>
              <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Month</TableCell>
                      <TableCell align="right">Total Calls</TableCell>
                      <TableCell align="right">Retrieval</TableCell>
                      <TableCell align="right">Submission</TableCell>
                      <TableCell align="right">Amendment</TableCell>
                      <TableCell align="right">Retraction</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {usageData.monthlyTrend.map((month) => (
                      <TableRow key={`${month.year}-${month.month}`}>
                        <TableCell>{new Date(month.year, month.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}</TableCell>
                        <TableCell align="right">{month.total.toLocaleString()}</TableCell>
                        <TableCell align="right">{month.retrieval.toLocaleString()}</TableCell>
                        <TableCell align="right">{month.submission.toLocaleString()}</TableCell>
                        <TableCell align="right">{month.amendment.toLocaleString()}</TableCell>
                        <TableCell align="right">{month.retraction.toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUsageDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Sync Confirmation Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => !syncLoading && setSyncDialogOpen(false)}
      >
        <DialogTitle>Sync Billing Data</DialogTitle>
        <DialogContent>
          <Typography>
            This will synchronize billing data for {selectedPeriod.month}/{selectedPeriod.year}.
            Are you sure you want to proceed?
          </Typography>
          {syncLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSyncDialogOpen(false)}
            disabled={syncLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSyncBilling}
            variant="contained"
            disabled={syncLoading}
          >
            Sync
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default BillingManagement; 